import { useState, useMemo } from "react";
import { Ticket, Check, Receipt, AlertCircle, Settings, Clock, Search } from "lucide-react";
import { useCommands } from "@/hooks/useCommands";
import { useEntryTypes, EntryType } from "@/hooks/useEntryTypes";
import { useCommandEntries, CommandEntry } from "@/hooks/useCommandEntries";
import { useCashRegister } from "@/hooks/useCashRegister";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ManageEntryDialog } from "@/components/commands/ManageEntryDialog";
import { cn } from "@/lib/utils";

export default function AberturaComandaPage() {
  const { commands, isLoading: isLoadingCommands } = useCommands();
  const { entryTypes, isLoading: isLoadingEntryTypes, getActiveEntryTypes } = useEntryTypes();
  const { openCommands, getActiveEntries, cancelEntry, updateEntry, isLoading: isLoadingEntries, refetch } = useCommandEntries();
  const { currentRegister } = useCashRegister();

  const [startNumber, setStartNumber] = useState("");
  const [endNumber, setEndNumber] = useState("");
  const [selectedCommands, setSelectedCommands] = useState<Set<string>>(new Set());
  const [selectedEntryType, setSelectedEntryType] = useState<EntryType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEntry, setSelectedEntry] = useState<CommandEntry | null>(null);
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);

  const activeEntryTypes = getActiveEntryTypes();
  const activeEntries = getActiveEntries();

  // Filter active entries by search
  const filteredActiveEntries = useMemo(() => {
    if (!searchQuery.trim()) return activeEntries;
    return activeEntries.filter((e) =>
      e.command_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.entry_type_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [activeEntries, searchQuery]);

  // Get available commands (active and available status)
  const availableCommands = useMemo(() => {
    return commands
      .filter((c) => c.is_active && c.status === "available")
      .sort((a, b) => {
        const numA = parseInt(a.number) || 0;
        const numB = parseInt(b.number) || 0;
        return numA - numB;
      });
  }, [commands]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleRangeChange = () => {
    const start = parseInt(startNumber) || 0;
    const end = parseInt(endNumber) || 0;

    if (start > 0 && end >= start) {
      const newSelected = new Set<string>();
      availableCommands.forEach((cmd) => {
        const num = parseInt(cmd.number) || 0;
        if (num >= start && num <= end) {
          newSelected.add(cmd.number);
        }
      });
      setSelectedCommands(newSelected);
    }
  };

  const handleSelectAll = () => {
    if (selectedCommands.size === availableCommands.length) {
      setSelectedCommands(new Set());
    } else {
      setSelectedCommands(new Set(availableCommands.map((c) => c.number)));
    }
  };

  const handleToggleCommand = (number: string) => {
    const newSelected = new Set(selectedCommands);
    if (newSelected.has(number)) {
      newSelected.delete(number);
    } else {
      newSelected.add(number);
    }
    setSelectedCommands(newSelected);
  };

  const handleOpenCommands = async () => {
    if (selectedCommands.size === 0 || !selectedEntryType) return;

    setIsSubmitting(true);
    await openCommands(
      Array.from(selectedCommands),
      {
        id: selectedEntryType.id,
        name: selectedEntryType.name,
        amount: selectedEntryType.amount,
        entry_mode: selectedEntryType.entry_mode,
      },
      currentRegister?.id
    );
    setIsSubmitting(false);
    setSelectedCommands(new Set());
    setSelectedEntryType(null);
    setStartNumber("");
    setEndNumber("");
    refetch();
  };

  const handleManageEntry = (entry: CommandEntry) => {
    setSelectedEntry(entry);
    setIsManageDialogOpen(true);
  };

  const handleCancelEntry = async (entryId: string, reason?: string) => {
    const result = await cancelEntry(entryId, reason);
    if (result && typeof result === 'object' && result.success) {
      refetch();
    }
    return result as { success: boolean; entry?: CommandEntry };
  };

  const handleUpdateEntry = async (
    entryId: string,
    newEntryType: { id: string; name: string; amount: number; entry_mode: "fixed" | "minimum_consumption" },
    reason?: string
  ) => {
    const result = await updateEntry(entryId, newEntryType, reason);
    if (result.success) {
      refetch();
    }
    return result;
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalAmount = selectedCommands.size * (selectedEntryType?.amount || 0);

  const isLoading = isLoadingCommands || isLoadingEntryTypes || isLoadingEntries;

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Receipt className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Abertura de Comandas</h1>
          <p className="text-sm text-muted-foreground">
            Selecione as comandas e o tipo de entrada para abrir
          </p>
        </div>
      </div>

      {!currentRegister && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            O caixa não está aberto. As entradas serão registradas sem vínculo com o caixa.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="abrir" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="abrir">
            <Receipt className="h-4 w-4 mr-2" />
            Abrir Comandas
          </TabsTrigger>
          <TabsTrigger value="ativas">
            <Settings className="h-4 w-4 mr-2" />
            Entradas Ativas ({activeEntries.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="abrir" className="mt-6">
          {activeEntryTypes.length === 0 && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Nenhum tipo de entrada cadastrado. Vá em Configurações → Tipos de Entrada para cadastrar.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Seleção de Comandas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Selecionar Comandas
                </CardTitle>
                <CardDescription>
                  Escolha as comandas que serão abertas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Range selector */}
                <div className="flex items-end gap-2">
                  <div className="space-y-2">
                    <Label>De</Label>
                    <Input
                      type="number"
                      min="1"
                      value={startNumber}
                      onChange={(e) => setStartNumber(e.target.value)}
                      className="w-24"
                      placeholder="001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Até</Label>
                    <Input
                      type="number"
                      min="1"
                      value={endNumber}
                      onChange={(e) => setEndNumber(e.target.value)}
                      className="w-24"
                      placeholder="100"
                    />
                  </div>
                  <Button variant="outline" onClick={handleRangeChange}>
                    Aplicar
                  </Button>
                  <Button variant="ghost" onClick={handleSelectAll}>
                    {selectedCommands.size === availableCommands.length
                      ? "Desmarcar Todas"
                      : "Selecionar Todas"}
                  </Button>
                </div>

            <div className="text-sm text-muted-foreground">
              {selectedCommands.size} de {availableCommands.length} comandas selecionadas
            </div>

            {/* Command grid */}
            <div className="max-h-80 overflow-y-auto border rounded-lg p-3">
              {availableCommands.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma comanda disponível
                </div>
              ) : (
                <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2">
                  {availableCommands.map((cmd) => (
                    <button
                      key={cmd.id}
                      onClick={() => handleToggleCommand(cmd.number)}
                      className={cn(
                        "h-10 rounded-md border text-sm font-medium transition-colors",
                        selectedCommands.has(cmd.number)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background hover:bg-muted border-border"
                      )}
                    >
                      {cmd.number.padStart(3, "0")}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Seleção de Tipo de Entrada */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              Tipo de Entrada
            </CardTitle>
            <CardDescription>
              Selecione o tipo de entrada a ser cobrado
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeEntryTypes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum tipo de entrada ativo</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {activeEntryTypes.map((entryType) => (
                  <button
                    key={entryType.id}
                    onClick={() => setSelectedEntryType(entryType)}
                    className={cn(
                      "flex items-center justify-between p-4 rounded-lg border transition-all text-left",
                      selectedEntryType?.id === entryType.id
                        ? "bg-primary/5 border-primary ring-2 ring-primary"
                        : "bg-background hover:bg-muted border-border"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "h-10 w-10 rounded-full flex items-center justify-center",
                          selectedEntryType?.id === entryType.id
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted"
                        )}
                      >
                        {selectedEntryType?.id === entryType.id ? (
                          <Check className="h-5 w-5" />
                        ) : (
                          <Ticket className="h-5 w-5" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium">{entryType.name}</div>
                        <Badge variant="secondary" className="text-xs mt-1">
                          {entryType.entry_mode === "fixed"
                            ? "Entrada Fixa"
                            : "Consumação Mínima"}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-xl font-bold">
                      {formatCurrency(entryType.amount)}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Summary */}
            {selectedCommands.size > 0 && selectedEntryType && (
              <div className="mt-6 p-4 rounded-lg bg-muted">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted-foreground">Resumo</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Comandas</span>
                    <span className="font-medium">{selectedCommands.size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Valor unitário</span>
                    <span className="font-medium">
                      {formatCurrency(selectedEntryType.amount)}
                    </span>
                  </div>
                  <div className="border-t pt-2 mt-2 flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-primary">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
              </div>
            )}

            <Button
              className="w-full"
              size="lg"
              disabled={
                selectedCommands.size === 0 || !selectedEntryType || isSubmitting
              }
              onClick={handleOpenCommands}
            >
              {isSubmitting ? (
                "Abrindo..."
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Abrir {selectedCommands.size} Comanda(s)
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </TabsContent>

    <TabsContent value="ativas" className="mt-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Entradas Ativas
          </CardTitle>
          <CardDescription>
            Gerencie as entradas ativas - altere ou cancele com estorno
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por comanda ou tipo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {filteredActiveEntries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma entrada ativa encontrada</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filteredActiveEntries.map((entry) => (
                <button
                  key={entry.id}
                  onClick={() => handleManageEntry(entry)}
                  className="flex flex-col p-4 rounded-lg border bg-background hover:bg-muted transition-colors text-left"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-lg">
                      {entry.command_number.padStart(3, "0")}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {entry.entry_mode === "fixed" ? "Fixa" : "Cons. Mín."}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {entry.entry_type_name}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-bold text-primary">
                      {formatCurrency(entry.amount)}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {entry.created_at ? formatDateTime(entry.created_at) : "-"}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  </Tabs>

  <ManageEntryDialog
    open={isManageDialogOpen}
    onOpenChange={setIsManageDialogOpen}
    entry={selectedEntry}
    entryTypes={entryTypes}
    onCancel={handleCancelEntry}
    onUpdate={handleUpdateEntry}
  />
    </div>
  );
}
